import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { PieChart } from '@mui/x-charts/PieChart';

/**
 * Componente que exibe um gráfico de pizza (pie chart) mostrando os motivos de descarte de leads.
 * Possui um menu lateral com legenda detalhada e porcentagens.
 * 
 * @param {Object} props
 * @param {Object} props.aggregatedDiscardData - Dados agregados dos descartes
 * @param {Array<string>} props.aggregatedDiscardData.labels - Array de strings com os nomes dos motivos de descarte
 * @param {Array<number>} props.aggregatedDiscardData.data - Array de números com os valores correspondentes a cada motivo
 */
const MotivoDeDescarteGrafico = ({ aggregatedDiscardData }) => {
    // Estados para controlar o menu de legenda
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    // Paleta de cores para o gráfico (10 cores diferentes)
    const colorPalette = [
        '#4caf50', '#2196f3', '#ff9800', '#e91e63',
        '#9c27b0', '#00bcd4', '#f44336', '#3f51b5',
        '#795548', '#607d8b'
    ];

    /**
     * Normaliza o nome removendo acentos, caracteres especiais e convertendo para minúsculas
     * para facilitar a comparação entre strings semelhantes.
     * 
     * @param {string} nome - Nome a ser normalizado
     * @returns {string} Nome normalizado
     */
    const normalizarNome = (nome) => {
        return nome
            .toLowerCase()                   // Converte para minúsculas
            .normalize('NFD')                // Normaliza decomposição canônica
            .replace(/[\u0300-\u036f]/g, '') // Remove acentos
            .replace(/[ç]/g, 'c')            // Substitui 'ç' por 'c'
            .replace(/[^a-z]/g, '')          // Remove caracteres não alfabéticos
            .trim();                         // Remove espaços extras
    };

    /**
     * Substitui frases semelhantes por uma versão padronizada
     * usando um mapeamento de equivalências.
     * 
     * @param {string} label - Texto original do motivo de descarte
     * @returns {string} Texto padronizado
     */
    const substituirFraseSemelhante = (label) => {
        const normalizado = normalizarNome(label);

        // Mapeamento de textos normalizados para textos padronizados
        const mapeamento = {
            'leadnaorespondeutentativasdecadencia': 'Lead não respondeu às tentativas de cadência',
            'leadnaorespondeucadencia': 'Lead não respondeu às tentativas de cadência',
            'semrespostacadença': 'Lead não respondeu às tentativas de cadência',
            'naorespondeuacadença': 'Lead não respondeu às tentativas de cadência',
            'naorespondeutentativasdecadencia': 'Lead não respondeu às tentativas de cadência',
            // Adicione outras variações conforme necessário
        };

        return mapeamento[normalizado] || label;
    };

    // Objetos para armazenar dados mapeados e originais
    const nomeMapeado = {};
    const nomeOriginal = {};

    // Processa os dados para agrupar motivos semelhantes
    aggregatedDiscardData.labels.forEach((label, index) => {
        // Padroniza o texto do motivo
        const adjustedLabel = substituirFraseSemelhante(label);
        const key = normalizarNome(adjustedLabel);

        // Se é a primeira ocorrência deste motivo, cria uma nova entrada
        if (!nomeMapeado[key]) {
            nomeMapeado[key] = {
                label: adjustedLabel,
                realValue: aggregatedDiscardData.data[index],
            };
            nomeOriginal[key] = adjustedLabel;
        } else {
            // Se já existe, soma o valor ao motivo existente
            nomeMapeado[key].realValue += aggregatedDiscardData.data[index];
        }
    });

    const total = Object.values(nomeMapeado).reduce((sum, item) => sum + item.realValue, 0);
    
    // Define o limite percentual abaixo do qual aplicamos o valor mínimo visual
    const minPercentageThreshold = 2; // 2%
    
    // Define o valor mínimo visual para fatias pequenas (apenas para exibição)
    const adjustedMinPercentage = 2; // 2% do total

    // Primeiro, calcula a porcentagem real de cada item
    const chartData = Object.entries(nomeMapeado).map(([key, item], index) => {
        const realValue = item.realValue;
        const percentage = total > 0 ? (realValue / total * 100) : 0;
        const percentageFormatted = percentage.toFixed(1);
        
        // Para valores visuais, aplicamos a lógica de ajuste apenas para itens muito pequenos
        let visualValue = realValue;
        if (percentage < minPercentageThreshold && percentage > 0) {
            // Calcula um valor visual mínimo baseado na porcentagem ajustada
            visualValue = (adjustedMinPercentage / 100) * total;
        }

        return {
            id: index,
            realValue,          // valor real para cálculos e exibição no tooltip/legenda
            value: visualValue,  // valor ajustado para renderização do gráfico
            percentage: percentageFormatted,
            label: nomeOriginal[key],
            color: colorPalette[index % colorPalette.length],
        };
    });
    
    // Recalcula o total visual após os ajustes (para normalização)
    const totalVisual = chartData.reduce((sum, item) => sum + item.value, 0);
    
    // Normaliza os valores visuais para que somem 100%
    chartData.forEach(item => {
        item.normalizedValue = (item.value / totalVisual) * total;
    });

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                position: 'relative',
            }}
        >
            {/* Botão de menu sanduíche para abrir a legenda */}
            <IconButton onClick={handleClick} aria-label="Abrir menu lateral" sx={{
                position: 'absolute',
                left: 16,
                top: 16,
                zIndex: 1,
            }}>
                <MenuIcon />
            </IconButton>

            {/* Menu dropdown da legenda com porcentagens */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                aria-label="Menu com legendas e porcentagens do gráfico de descartes"
            >
                {chartData
                    .sort((a, b) => b.realValue - a.realValue)
                    .map((item, index) => (
                        <MenuItem key={index}>
                            <ListItemIcon>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: item.color,
                                        borderRadius: 1,
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: 13,
                                            whiteSpace: 'normal',     // Permite quebra de linha
                                            wordBreak: 'break-word',  // Quebra palavras longas se necessário
                                            display: 'inline-block',  // Faz o texto se comportar como um bloco limitado
                                        }}
                                    >
                                        <strong>{item.label}</strong>: {item.realValue} ({item.percentage}%)
                                    </Typography>
                                }
                            />
                        </MenuItem>
                    ))}
            </Menu>

            {/* Container do gráfico de pizza */}
            <Box
                sx={{
                    width: { xs: '100%', sm: 300 }, // Responsivo: 100% em mobile, 300px em tablet+
                    height: 300,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto', // Centraliza horizontalmente
                }}
            >
                <PieChart
                    series={[
                        {
                            data: chartData.map((item) => ({
                                ...item,
                                value: item.normalizedValue,  // Usa o valor normalizado para visualização
                            })),
                            outerRadius: 100,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                            faded: { additionalRadius: -10, color: 'gray' },
                            valueFormatter: (item) => `${item.realValue} (${item.percentage}%)`,
                            arcLabel: (item) => {
                                // Usa o valor real para decidir se mostra a label
                                const realPercentage = (item.realValue / total) * 100;
                                if (realPercentage < 5) return ''; // Não mostra label para fatias muito pequenas
                                return `${item.percentage}%`;
                            },
                            arcLabelRadius: 125,
                            color: ({ id }) => chartData[id].color,
                        },
                    ]}
                    width={250}
                    height={300}
                    margin={{ top: 10, right: 30, bottom: 10, left: 30 }}
                    slotProps={{
                        legend: {
                            hidden: true,
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default MotivoDeDescarteGrafico;